import {ToastType} from '@sportcast/ui-react';
import i18n from 'i18next';
import {AppDispatch} from '../store';
import {MessageProps} from '../components/alert/Message';
import {hideLoader} from '../redux/slice/loaderSlice';
import {setMessage} from '../redux/slice/messageSlice';

export interface ErrorJson {
    errorResponse?: {
        errorCode?: number;
        message?: string;
    };
}

export type StatusHandler = (json: ErrorJson) => MessageProps | undefined;

export interface ErrorJsonHandlerMap {
    [key: number]: StatusHandler;
}

const requestErrorHandler = (req: Promise<unknown>, method: string, dispatch: AppDispatch, statusHandler: ErrorJsonHandlerMap = {}) => {
    return req.catch((error: Response) => {
        let errorMessage: MessageProps;

        if (error.status === 401) {
            errorMessage = {
                message: 'No session.',
                i18nMessage: 'error.NO_SESSION',
                autoClose: false,
                type: ToastType.ERROR
            };
        } else if (error.status === 403) {
            // Permission denied from backend, this should never be called for the currently logged-in user
            errorMessage = {
                message: i18n.t('error.NO_ACCESS', {method, url: error.url}),
                autoClose: true,
                type: ToastType.ERROR
            };
        } else if (error.status === 404) {
            errorMessage = {
                message: i18n.t('error.NOT_FOUND'),
                autoClose: true,
                type: ToastType.ERROR
            };
        } else if (error.status === 500) {
            errorMessage = {
                message: i18n.t('error.INTERNAL_SERVER_ERROR'),
                i18nMessage: 'error.INTERNAL_SERVER_ERROR',
                autoClose: true,
                type: ToastType.ERROR,
                autoCloseDuration: 5000
            };
        } else if (error.body !== null && typeof error.json === 'function') {
            return error.json().then(json => {
                if (error.status && Object.hasOwn(statusHandler, error.status)) {
                    const handler = statusHandler[error.status];
                    const message = handler?.(json);
                    if (message) {
                        dispatch(setMessage(message));
                    }
                } else if (json.errorResponse?.message) {
                    if (error.status === 1002) {
                        dispatch(setMessage({
                            message: extractContent(JSON.stringify(json.errorResponse.message)),
                            autoClose: true,
                            type: ToastType.ERROR,
                            autoCloseDuration: 5000,
                            i18nMessage: `errorCode.${json.errorResponse.errorCode}`
                        }));
                    } else {
                        dispatch(setMessage({
                            message: JSON.stringify(json.errorResponse.message),
                            autoClose: true,
                            type: ToastType.ERROR,
                            autoCloseDuration: 5000,
                            i18nMessage: `errorCode.${json.errorResponse.errorCode}`
                        }));
                    }
                } else {
                    dispatch(setMessage({
                        message: i18n.t('error.ERROR_CALLING_BACKEND', {code: error.status, method: method, url: error.url}),
                        autoClose: true,
                        type: ToastType.ERROR,
                        autoCloseDuration: 5000
                    }));
                }
                return Promise.resolve(json);
            }).catch((error) => {
                dispatch(setMessage({
                    message: error.message,
                    autoClose: true,
                    type: ToastType.ERROR,
                    autoCloseDuration: 5000
                }));
            }).finally(() => dispatch(hideLoader()));
        } else if (!error.text || typeof error.text !== 'function') {
            errorMessage = {
                message: 'error',
                autoClose: true,
                type: ToastType.ERROR,
                autoCloseDuration: 5000
            };
        } else {
            const data = error.text();
            errorMessage = {
                message: JSON.stringify(data),
                autoClose: true,
                type: ToastType.ERROR,
                autoCloseDuration: 5000
            };
        }

        console.error('Can not push data. ' + JSON.stringify(error));
        dispatch(setMessage(errorMessage));
        dispatch(hideLoader());
        return errorMessage;
    });
};

function extractContent(str: string) {
    const regex = /\[(.*?)]/;
    const match = RegExp(regex).exec(str);

    return match ? match[1].trim() : '';
}

export default requestErrorHandler;
